// Rendered notification content for the admin audit surfaces.
//
// The audit list (`/admin/notifications/audit`) reads the in-app
// `notifications` table, which stores the headline `message` but NOT the
// rendered email HTML / SMS / push bodies the customer actually received.
// Those bodies are only materialised per `comm_flow_runs` row and are
// surfaced — already rendered — by `GET /admin/trips/:id/notifications`
// (see fetchTripNotifications). Rather than reimplement template rendering
// behind a new render-by-notification-id endpoint (legacy email/SMS bodies
// are not persisted by design), we reuse that trip-scoped endpoint: when an
// audit row carries a trip reference we fetch the trip's notification runs
// and show the real rendered content.
//
// This module is the thin client layer for that reuse path plus the shared
// content shape consumed by EmailPreviewDialog.

import { fetchTripNotifications } from "./trips";
import type { TripNotificationsResponse, TripNotificationContent } from "./types";

/** A single channel's rendered content (email/SMS/push). */
export interface RenderedNotificationContent {
  email?: { subject: string; html: string };
  sms?: { body: string };
  push?: { title: string; body: string; deepLink?: string | null };
  /** Surfaced when no channel body could be rendered. */
  note?: string;
}

/** One rendered run, with enough metadata to label it in the dialog. */
export interface RenderedNotificationRun {
  runId: number;
  flowId: string;
  channel: "email" | "sms" | "push";
  status: string;
  templateId?: string | null;
  createdAt: string;
  content: RenderedNotificationContent;
}

export interface RenderedNotificationContentResponse {
  tripId: number;
  publicId: string | null;
  runs: RenderedNotificationRun[];
}

function toRendered(c: TripNotificationContent | undefined): RenderedNotificationContent {
  if (!c) return { note: "No rendered content available." };
  return { email: c.email, sms: c.sms, push: c.push, note: c.note };
}

/**
 * Fetch the rendered email/SMS/push content for every notification run on a
 * trip, by reusing the existing trip-notifications endpoint. The audit UI
 * uses this to show the actual content a customer received for a given
 * notification row that is scoped to a trip.
 */
export async function fetchNotificationRenderedContent(
  token: string,
  tripId: number,
): Promise<RenderedNotificationContentResponse> {
  const res: TripNotificationsResponse = await fetchTripNotifications(token, tripId);
  const runs: RenderedNotificationRun[] = res.runs.map((run) => ({
    runId: run.id,
    flowId: run.flowId,
    channel: run.channel,
    status: run.status,
    templateId: run.templateId,
    createdAt: run.createdAt,
    content: toRendered(res.content[run.id]),
  }));
  return { tripId: res.tripId, publicId: res.publicId, runs };
}
